import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Plus, Edit, Trash2, ToggleLeft, ToggleRight, Calendar, Clock,
  X, Upload, Image, Stethoscope, Heart, Pill, Syringe,
  Activity, Bed, Thermometer, Baby, Eye, Bone, Shield,
  ClipboardList, Users, Phone, FileText, Home, Search, Filter,
  ChevronLeft, ChevronRight, ChevronDown
} from 'lucide-react'
import { eventsApi, uploadApi, hospitalsApi } from '../../lib/api'

interface EventItem {
  id: string
  name: string
  points: number
  description?: string
  icon?: string
  is_active: boolean
  cycle?: 'day' | 'week' | 'month' | 'quarter' | 'year'
  cycle_limit?: number
  max_total_count?: number
  sort_order?: number
  hospital_ids?: string[]
  hospital_names?: string[]
  created_at?: string
  creator_hospital_id?: string
}

interface Hospital {
  id: string
  name: string
}

const Events = () => {
  const [events, setEvents] = useState<EventItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [keyword, setKeyword] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState<'add' | 'edit'>('add')
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null)
  const [deletingEvent, setDeletingEvent] = useState<EventItem | null>(null)
  const [formLoading, setFormLoading] = useState(false)

  const [formData, setFormData] = useState({
    name: '', points: '', description: '', icon: '',
    cycle: 'day', cycle_limit: '', max_total_count: '', sort_order: '0', hospital_ids: [] as string[]
  })
  const [hospitalExpanded, setHospitalExpanded] = useState(false)

  const [isActive, setIsActive] = useState(true)
  const [iconUploading, setIconUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [hospitals, setHospitals] = useState<Hospital[]>([])

  // 当前是否为机构负责人（从 auth_user 读取，auth.tsx 登录时写入的 key）
  const rawAuthUser = localStorage.getItem('auth_user')
  const authUser = rawAuthUser ? JSON.parse(rawAuthUser) : null
  console.log('=== Events 页面调试 ===')
  console.log('auth_user:', rawAuthUser)
  console.log('authUser 解析后:', authUser)
  console.log('authUser.role:', authUser?.role)
  console.log('authUser.medicalRole:', authUser?.medicalRole)
  console.log('authUser.hospitalId:', authUser?.hospitalId)
  const isSupervisor = authUser?.role === 'medical' && authUser?.medicalRole === 'supervisor'
  const isAdmin = authUser?.role === 'admin'
  // 机构负责人默认绑定自己机构
  const supervisorHospitalId = authUser?.hospitalId || null

  const totalPages = Math.ceil(total / pageSize)

  const fetchEvents = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params: Record<string, unknown> = { page: currentPage, limit: pageSize }
      if (keyword) params.keyword = keyword
      const res = await eventsApi.list(params) as { total: number; event_types?: any[]; items?: any[] }
      const rawItems = res.items || res.event_types || []
      const items = rawItems.map((e: any) => ({ ...e, is_active: Boolean(e.enabled) }))
      setEvents(items)
      setTotal(res.total || items.length)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [currentPage, keyword])

  useEffect(() => { fetchEvents() }, [fetchEvents])

  useEffect(() => {
    hospitalsApi.list().then((res: any) => {
      setHospitals(res.hospitals || [])
    }).catch(() => {})
  }, [])

  useEffect(() => {
    const t = setTimeout(() => setCurrentPage(1), 400)
    return () => clearTimeout(t)
  }, [keyword])

  const presetIcons: { Icon: React.FC<{ className?: string }>; label: string; iconUrl: string }[] = [
    { Icon: Calendar, label: '日历', iconUrl: '/static/icons/calendar.svg' },
    { Icon: Stethoscope, label: '听诊', iconUrl: '/static/icons/stethoscope.svg' },
    { Icon: Heart, label: '心脏', iconUrl: '/static/icons/heart.svg' },
    { Icon: Pill, label: '药品', iconUrl: '/static/icons/pill.svg' },
    { Icon: Syringe, label: '注射', iconUrl: '/static/icons/syringe.svg' },
    { Icon: Activity, label: '活动', iconUrl: '/static/icons/activity.svg' },
    { Icon: Bed, label: '床位', iconUrl: '/static/icons/bed.svg' },
    { Icon: Thermometer, label: '体温', iconUrl: '/static/icons/thermometer.svg' },
    { Icon: Baby, label: '婴儿', iconUrl: '/static/icons/baby.svg' },
    { Icon: Eye, label: '眼科', iconUrl: '/static/icons/eye.svg' },
    { Icon: Bone, label: '骨科', iconUrl: '/static/icons/bone.svg' },
    { Icon: Clock, label: '时间', iconUrl: '/static/icons/clock.svg' },
    { Icon: Shield, label: '防护', iconUrl: '/static/icons/shield.svg' },
    { Icon: ClipboardList, label: '档案', iconUrl: '/static/icons/clipboard-list.svg' },
    { Icon: Users, label: '人群', iconUrl: '/static/icons/users.svg' },
    { Icon: Phone, label: '电话', iconUrl: '/static/icons/phone.svg' },
    { Icon: FileText, label: '报告', iconUrl: '/static/icons/file-text.svg' },
    { Icon: Home, label: '家庭', iconUrl: '/static/icons/home.svg' },
  ]

  const openAdd = () => {
    setModalType('add')
    // supervisor 添加时自动绑定本机构
    const defaultHospitalIds = isSupervisor && supervisorHospitalId ? [supervisorHospitalId] : []
    setFormData({ name: '', points: '', description: '', icon: '', cycle: 'day', cycle_limit: '', max_total_count: '', sort_order: '0', hospital_ids: defaultHospitalIds })
    setHospitalExpanded(false)
    setIsActive(true)
    setShowModal(true)
  }

  const openEdit = (event: EventItem) => {
    // 机构负责人不能编辑总后台创建的事件
    if (isSupervisor && !event.creator_hospital_id) {
      alert('总后台创建的事件类型仅限查看，不可编辑')
      return
    }
    setSelectedEvent(event)
    setModalType('edit')
    setFormData({
      name: event.name,
      points: String(event.points),
      description: event.description || '',
      icon: event.icon || '',
      cycle: event.cycle || 'day',
      cycle_limit: String(event.cycle_limit || ''),
      max_total_count: String(event.max_total_count || ''),
      sort_order: String(event.sort_order ?? 0),
      hospital_ids: event.hospital_ids || []
    })
    setHospitalExpanded(false)
    setIsActive(event.is_active)
    setShowModal(true)
  }

  const handleSubmit = async () => {
    if (!formData.name) { alert('请输入事件名称'); return }
    if (!formData.points || parseInt(formData.points) <= 0) { alert('请输入有效的积分'); return }
    setFormLoading(true)
    try {
      const payload = {
        name: formData.name,
        points: parseInt(formData.points),
        description: formData.description,
        icon: formData.icon || undefined,
        cycle: formData.cycle,
        cycle_limit: formData.cycle_limit ? parseInt(formData.cycle_limit) : undefined,
        max_total_count: formData.max_total_count ? parseInt(formData.max_total_count) : undefined,
        sort_order: parseInt(formData.sort_order) || 0,
        hospital_ids: formData.hospital_ids.length > 0 ? formData.hospital_ids : undefined,
      }
      if (modalType === 'add') {
        await eventsApi.create(payload)
      } else if (selectedEvent) {
        await eventsApi.update(selectedEvent.id, payload)
      }
      setShowModal(false)
      fetchEvents()
    } catch (e) {
      alert((e as Error).message)
    } finally {
      setFormLoading(false)
    }
  }

  const handleToggle = async (event: EventItem) => {
    try {
      await eventsApi.toggle(event.id, !event.is_active)
      fetchEvents()
    } catch (e) {
      alert((e as Error).message)
    }
  }

  const handleDelete = async () => {
    if (!deletingEvent) return
    try {
      await eventsApi.delete(deletingEvent.id)
      setDeletingEvent(null)
      fetchEvents()
    } catch (e) {
      alert((e as Error).message)
    }
  }

  const handleIconSelect = (iconUrl: string) => {
    setFormData({ ...formData, icon: iconUrl })
  }

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { alert('请选择图片文件'); return }
    if (file.size > 5 * 1024 * 1024) { alert('图片大小不能超过5MB'); return }
    setIconUploading(true)
    try {
      const url = await uploadApi.uploadImage(file)
      setFormData({ ...formData, icon: url })
    } catch (err) {
      alert((err as Error).message)
    } finally {
      setIconUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">事件类型管理</h1>
          <p className="text-gray-500 text-sm mt-1">共 {total} 种事件类型</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 bg-green-700 text-white px-4 py-2 rounded-xl hover:bg-green-800">
          <Plus size={18} /> 添加事件
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input type="text" placeholder="搜索事件名称..."
            value={keyword} onChange={e => setKeyword(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-400">加载中...</div>
      ) : error ? (
        <div className="text-center py-20 text-red-500">{error}</div>
      ) : events.length === 0 ? (
        <div className="text-center py-20 text-gray-400">暂无数据</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {events.map(event => (
            <div key={event.id} className={`bg-white rounded-2xl shadow-md p-6 ${event.is_active ? '' : 'opacity-60'}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${event.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                    {(event as any).icon ? (
                      <img src={(event as any).icon} alt="" className="w-8 h-8 object-contain" />
                    ) : (
                      <Calendar className={`w-6 h-6 ${event.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">{event.name}</h3>
                    <p className="text-sm text-gray-500">{event.description || '暂无描述'}</p>
                  </div>
                </div>
                {/* 机构负责人查看总后台事件：隐藏切换按钮 */}
                {!(isSupervisor && !event.creator_hospital_id) && (
                  <button onClick={() => handleToggle(event)} className="focus:outline-none">
                    {event.is_active
                      ? <ToggleRight className="w-10 h-10 text-green-600" />
                      : <ToggleLeft className="w-10 h-10 text-gray-300" />}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-green-600">{event.points}</p>
                  <p className="text-xs text-gray-500">积分</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-gray-700">{event.cycle === 'day' ? '每天' : event.cycle === 'week' ? '每周' : event.cycle === 'month' ? '每月' : event.cycle === 'quarter' ? '每季度' : '每年'}</p>
                  <p className="text-xs text-gray-500">周期</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-gray-700">{event.cycle_limit ?? '-'}</p>
                  <p className="text-xs text-gray-500">周期限次</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-blue-600">{event.max_total_count ?? '-'}</p>
                  <p className="text-xs text-gray-500">累计限次</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                {!event.creator_hospital_id && (
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">总后台添加</span>
                )}
                {event.hospital_names && event.hospital_names.length > 0 && (
                  <div>
                    <span className="font-medium">限定医院：</span>
                    {event.hospital_names.join('、')}
                  </div>
                )}
                <div className="ml-auto">
                  <span className="font-medium">排序：</span>
                  <span className="text-orange-600 font-bold">{event.sort_order ?? 0}</span>
                </div>
              </div>
              <div className="flex gap-2 pt-3 border-t">
                {isSupervisor && !event.creator_hospital_id ? (
                  // 机构负责人看总后台事件：只读
                  <button disabled className="flex-1 flex items-center justify-center gap-1 py-2 text-gray-300 rounded-lg cursor-not-allowed">
                    <Edit size={14} />查看
                  </button>
                ) : (
                  <>
                    <button onClick={() => openEdit(event)}
                      className="flex-1 flex items-center justify-center gap-1 py-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                      <Edit size={14} />编辑
                    </button>
                    <button onClick={() => setDeletingEvent(event)}
                      className="flex-1 flex items-center justify-center gap-1 py-2 text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 size={14} />删除
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">第 {currentPage} / {totalPages} 页，共 {total} 条</span>
          <div className="flex gap-2">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(currentPage - 2, totalPages - 4)) + i
              return p <= totalPages ? (
                <button key={p} onClick={() => setCurrentPage(p)}
                  className={`px-3 py-1.5 border rounded-lg ${p === currentPage ? 'bg-green-600 text-white' : 'hover:bg-gray-50'}`}>
                  {p}
                </button>
              ) : null
            })}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* 添加/编辑弹窗 */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-bold">{modalType === 'add' ? '添加事件类型' : '编辑事件类型'}</h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">事件图标</label>
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
                    {iconUploading ? (
                      <span className="text-xs text-gray-400">上传中...</span>
                    ) : formData.icon ? (
                      <img src={formData.icon} alt="" className="w-10 h-10 object-contain" />
                    ) : (
                      <Image className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg cursor-pointer hover:bg-green-100 transition-colors text-sm">
                      <Upload size={14} />
                      {iconUploading ? '上传中...' : '自定义上传'}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleIconUpload}
                        className="hidden"
                      />
                    </label>
                    {formData.icon && (
                      <button onClick={() => setFormData({ ...formData, icon: '' })} className="text-xs text-red-500 hover:text-red-700 text-center">清除图标</button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {presetIcons.map(({ Icon, label, iconUrl }) => (
                    <button key={label} type="button" title={label} onClick={() => handleIconSelect(iconUrl)}
                      className="w-10 h-10 bg-green-50 rounded-lg hover:bg-green-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-green-600" />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">事件名称 <span className="text-red-500">*</span></label>
                <input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="请输入事件名称"
                  className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">事件描述</label>
                <input value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="请输入事件描述"
                  className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">积分分值 <span className="text-red-500">*</span></label>
                  <input type="number" value={formData.points} onChange={e => setFormData({ ...formData, points: e.target.value })}
                    placeholder="请输入积分"
                    className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">排序数值</label>
                  <input type="number" value={formData.sort_order} onChange={e => setFormData({ ...formData, sort_order: e.target.value })}
                    placeholder="越大越靠前"
                    className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">周期限次</label>
                  <input type="number" value={formData.cycle_limit} onChange={e => setFormData({ ...formData, cycle_limit: e.target.value })}
                    placeholder="留空则不限制"
                    className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">累计最多参与次数</label>
                  <input type="number" value={formData.max_total_count} onChange={e => setFormData({ ...formData, max_total_count: e.target.value })}
                    placeholder="留空则不限制"
                    className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">积分周期</label>
                <select value={formData.cycle} onChange={e => setFormData({ ...formData, cycle: e.target.value })}
                  className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="day">每天</option>
                  <option value="week">每周</option>
                  <option value="month">每月</option>
                  <option value="quarter">每季度</option>
                  <option value="year">每年</option>
                </select>
              </div>
              {/* supervisor 添加事件时隐藏机构选择，自动绑定本机构 */}
              {!isSupervisor && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium">指定医院（可多选）</label>
                    {hospitals.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setHospitalExpanded(!hospitalExpanded)}
                        className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
                      >
                        {hospitalExpanded ? '收起' : '展开'}
                        <ChevronDown size={14} className={`transition-transform ${hospitalExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                  </div>
                  {hospitals.length === 0 ? (
                    <p className="text-sm text-gray-400 py-2">暂无可选医院</p>
                  ) : (
                    <div className={`flex flex-wrap gap-2 transition-all ${hospitalExpanded ? '' : 'max-h-12 overflow-hidden'}`}>
                      {hospitals.map(h => (
                        <button
                          key={h.id}
                          type="button"
                          onClick={() => {
                            const ids = formData.hospital_ids.includes(h.id)
                              ? formData.hospital_ids.filter(id => id !== h.id)
                              : [...formData.hospital_ids, h.id]
                            setFormData({ ...formData, hospital_ids: ids })
                          }}
                          className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                            formData.hospital_ids.includes(h.id)
                              ? 'bg-green-100 border-green-400 text-green-700'
                              : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {h.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-1">不选则所有医院均可参加此事件</p>
                </div>
              )}
              {isSupervisor && (
                <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                  <Shield size={14} className="text-green-600" />
                  <span>本机构创建，绑定机构：<strong className="text-gray-700">{authUser?.hospitalName || '本机构'}</strong></span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <button onClick={() => setIsActive(!isActive)}
                  className={`w-12 h-6 rounded-full transition-colors ${isActive ? 'bg-green-600' : 'bg-gray-300'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${isActive ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
                <span className="text-sm text-gray-600">启用此事件类型</span>
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2 border rounded-xl hover:bg-gray-50">取消</button>
              <button onClick={handleSubmit} disabled={formLoading}
                className="flex-1 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 disabled:opacity-50">
                {formLoading ? '提交中...' : modalType === 'add' ? '添加' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认 */}
      {deletingEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-bold mb-2">确认删除</h3>
            <p className="text-gray-500 text-sm mb-6">
              确定要删除事件类型 <strong>{deletingEvent.name}</strong> 吗？删除后无法恢复。
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeletingEvent(null)} className="flex-1 py-2 border rounded-xl hover:bg-gray-50">取消</button>
              <button onClick={handleDelete} className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Events
